import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { useFlags } from "launchdarkly-react-client-sdk";

import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";

import { getAdminSettings } from "sj-ui-toolkit/src/api/admin/common";
import { getApiKeys, getIdpSettings, getTenantIdp } from "sj-ui-toolkit/src/api/admin/settings";
import { getSiteSettings } from "sj-ui-toolkit/src/api/siteSettings";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getIdpAdminClient } from "sj-ui-toolkit/src/client";
import { getApiTokensClient } from "sj-ui-toolkit/src/client/svcApiTokens";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { fetchPasswordAuthEnabled } from "@/api/settings";
import { useAspenFeatures } from "@/hooks/useAspenFeatures";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConnectionCardProps {
  title: string;
  icon: string;
  status: React.ReactNode;
  details: string;
  path: string;
  testId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatusTag({ enabled, label }: { enabled: boolean; label?: string }) {
  return (
    <Tag
      value={label ?? (enabled ? "Enabled" : "Disabled")}
      severity={enabled ? "success" : "secondary"}
    />
  );
}

function ConnectionCard({ title, icon, status, details, path, testId }: ConnectionCardProps) {
  const navigate = useNavigate();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(path);
    }
  };

  return (
    <Card
      className="cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => navigate(path)}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={title}
      pt={{ root: { "data-testid": testId } }}
    >
      <div className="flex flex-col gap-3">
        <div>{status}</div>
        <div className="flex items-center gap-2">
          <i className={`pi ${icon} text-xl`} />
          <span className="font-semibold text-lg">{title}</span>
        </div>
        <div className="body-sm text-secondary break-all">{details}</div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Connections = () => {
  useBreadcrumb({
    label: "Connections",
    route: AdminAppRoutes.routes.connections.route,
    display: "none",
    resetTrail: true,
  });

  const { useMigratedApiKeys, useNewCognitoAuthSetting, mcpOauthClients, mcpAuthShowApiKey } =
    useFlags();

  const [loading, setLoading] = useState(true);
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoDomainCount, setSsoDomainCount] = useState(0);
  const [passwordAuthEnabled, setPasswordAuthEnabled] = useState(true);
  const [scimEnabled, setScimEnabled] = useState(false);
  const [aspenTokenCount, setAspenTokenCount] = useState(0);
  const [mcpClientCount, setMcpClientCount] = useState(0);
  const [mcpLoading, setMcpLoading] = useState(true);
  const [mcpError, setMcpError] = useState(false);
  const [reportingApiKeyCount, setReportingApiKeyCount] = useState(0);
  const [xapiAuthType, setXapiAuthType] = useState("");
  const [xapiConfigured, setXapiConfigured] = useState(false);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookEndpoint, setWebhookEndpoint] = useState("");

  const { isRestrictedBundle } = useUserContext();

  const { isBackOfficeAspenEnabled, isLoading: aspenFeaturesLoading } = useAspenFeatures({
    enabled: !isRestrictedBundle,
  });
  const load = useCallback(async () => {
    const apiTokensClient = getApiTokensClient();
    const currentTenantName = getCurrentSelectedTenant();

    try {
      const results = await Promise.allSettled([
        // SSO
        (async () => {
          if (!currentTenantName) return;
          const { data: idpData, error: idpError } = await getTenantIdp(currentTenantName);
          if (idpError) throw idpError;
          setSsoDomainCount(idpData?.domains?.length ?? 0);
          if (idpData?.idp?.uuid) {
            const { data: settings, error: settingsError } = await getIdpSettings(
              currentTenantName,
              idpData.idp.uuid
            );
            if (settingsError) throw settingsError;
            setSsoEnabled(settings?.tenantIdpSettings?.enabled ?? false);
          }
        })(),
        // Password auth
        (async () => {
          if (!useNewCognitoAuthSetting || !currentTenantName) return;
          setPasswordAuthEnabled(await fetchPasswordAuthEnabled(currentTenantName));
        })(),
        // SCIM
        apiTokensClient.listApiTokensWithEntitlements(["scim"]).then(({ data }) => {
          setScimEnabled((data?.apiTokens?.length ?? 0) > 0);
        }),
        // Aspen keys
        apiTokensClient
          .listApiTokensWithEntitlements([
            "github_action",
            "guardian",
            "aspen_read",
            "aspen_write",
            "aspen_read_write",
          ])
          .then(({ data }) => {
            setAspenTokenCount(data?.apiTokens?.length ?? 0);
          }),
        // Reporting API keys
        useMigratedApiKeys
          ? apiTokensClient.listApiTokensWithEntitlements(["reporting_api"]).then(({ data }) => {
              setReportingApiKeyCount(data?.apiTokens?.length ?? 0);
            })
          : getApiKeys().then(data => {
              setReportingApiKeyCount(data?.apiKeys?.length ?? 0);
            }),
        // Admin settings (xAPI + webhooks)
        getAdminSettings().then(data => {
          setXapiAuthType(data?.xapiAuthenticationType || "basic");
          setXapiConfigured(!!data?.xapiLrsUrl);
          setWebhookEnabled(!!data?.webhookEnabled);
        }),
        // Site settings (webhook endpoint)
        getSiteSettings().then(data => {
          setWebhookEndpoint(data?.site?.enrollmentCompletionNotificationEndpoint || "");
        }),
      ]);

      results.forEach(r => {
        if (r.status === "rejected") console.error("Connection data load failed:", r.reason);
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Loaded outside `load` and keyed on the flag: the mcp-oauth-clients flag may
  // resolve after mount, and svc-idp rejects these calls while it is off. The
  // count only renders once both sources succeed — a partial count would read
  // as a real state.
  useEffect(() => {
    const currentTenantName = getCurrentSelectedTenant();
    if (!mcpOauthClients || !currentTenantName) return;
    let cancelled = false;
    setMcpLoading(true);
    setMcpError(false);
    Promise.all([
      getIdpAdminClient()
        .listOAuthClientRegistrations(currentTenantName)
        .then(({ data, error }) => {
          if (error) throw error;
          return data?.clients?.filter(c => !c.deletedAt).length ?? 0;
        }),
      // API-keys are under a separate FF so that the count is only included if the flag is on
      mcpAuthShowApiKey
        ? getApiTokensClient()
            .listApiTokensWithEntitlements(["mcp"])
            .then(({ data, error }) => {
              if (error) throw error;
              return data?.apiTokens?.length ?? 0;
            })
        : Promise.resolve(0),
    ])
      .then(([clientCount, keyCount]) => {
        if (cancelled) return;
        setMcpClientCount(clientCount + keyCount);
        setMcpLoading(false);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        console.error("Connection data load failed:", reason);
        setMcpError(true);
        setMcpLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mcpOauthClients, mcpAuthShowApiKey]);

  if (loading) {
    return (
      <>
        <div className="mb-4">
          <h1>Connections</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} width="100%" height="176px" borderRadius="12px" />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-4">
        <h1>Connections</h1>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
        {!isRestrictedBundle && (
          <ConnectionCard
            title={useNewCognitoAuthSetting ? "Login Settings" : "SSO"}
            icon="pi-sign-in"
            testId="connection-card-sso"
            path={
              useNewCognitoAuthSetting
                ? AdminAppRoutes.routes.connections.routes.loginSettings.route
                : AdminAppRoutes.routes.connections.routes.sso.route
            }
            status={
              useNewCognitoAuthSetting ? (
                <div className="flex flex-wrap gap-2">
                  <StatusTag
                    enabled={passwordAuthEnabled}
                    label={`Password ${passwordAuthEnabled ? "Enabled" : "Disabled"}`}
                  />
                  <StatusTag
                    enabled={ssoEnabled}
                    label={`SSO ${ssoEnabled ? "Enabled" : "Disabled"}`}
                  />
                </div>
              ) : (
                <StatusTag enabled={ssoEnabled} />
              )
            }
            details={
              ssoEnabled
                ? `${ssoDomainCount} domain${ssoDomainCount !== 1 ? "s" : ""} configured`
                : "Single sign-on is not configured"
            }
          />
        )}

        {!isRestrictedBundle && (
          <ConnectionCard
            title="SCIM"
            icon="pi-users"
            testId="connection-card-scim"
            path={AdminAppRoutes.routes.connections.routes.scim.route}
            status={<StatusTag enabled={scimEnabled} />}
            details={
              scimEnabled
                ? "User provisioning is active"
                : "Automated user provisioning is not configured"
            }
          />
        )}

        {!isRestrictedBundle && !aspenFeaturesLoading && isBackOfficeAspenEnabled && (
          <ConnectionCard
            title="Aspen Integrations"
            icon="pi-key"
            testId="connection-card-aspen"
            path={AdminAppRoutes.routes.connections.routes.aspen.route}
            status={
              <StatusTag
                enabled={aspenTokenCount > 0}
                label={aspenTokenCount > 0 ? `${aspenTokenCount} active` : "Disabled"}
              />
            }
            details={
              aspenTokenCount > 0
                ? `${aspenTokenCount} Aspen key${aspenTokenCount !== 1 ? "s" : ""} configured`
                : "Aspen integration keys are not configured"
            }
          />
        )}

        {mcpOauthClients && mcpLoading && (
          <Skeleton width="100%" height="176px" borderRadius="12px" />
        )}

        {mcpOauthClients && !mcpLoading && (
          <ConnectionCard
            title="MCP"
            icon="pi-microchip-ai"
            testId="connection-card-mcp"
            path={AdminAppRoutes.routes.connections.routes.mcp.route}
            status={
              <div className="flex flex-wrap gap-2">
                {mcpError ? (
                  <Tag value="Unavailable" severity="warning" />
                ) : (
                  <StatusTag
                    enabled={mcpClientCount > 0}
                    label={mcpClientCount > 0 ? `${mcpClientCount} active` : "Disabled"}
                  />
                )}
                <Tag value="Beta" severity="info" />
              </div>
            }
            details={
              mcpError
                ? "MCP connection status couldn't be loaded"
                : mcpClientCount > 0
                  ? `${mcpClientCount} MCP connection${mcpClientCount !== 1 ? "s" : ""} configured`
                  : "MCP connections are not configured"
            }
          />
        )}

        <ConnectionCard
          title="Reporting API"
          icon="pi-chart-line"
          testId="connection-card-reporting-api"
          path={AdminAppRoutes.routes.connections.routes.reportingApi.route}
          status={
            <StatusTag
              enabled={reportingApiKeyCount > 0}
              label={reportingApiKeyCount > 0 ? `${reportingApiKeyCount} active` : "Disabled"}
            />
          }
          details={
            reportingApiKeyCount > 0
              ? `${reportingApiKeyCount} Reporting API key${reportingApiKeyCount !== 1 ? "s" : ""} configured`
              : "Reporting API keys are not configured"
          }
        />

        <ConnectionCard
          title="xAPI / LRS"
          icon="pi-database"
          testId="connection-card-xapi"
          path={AdminAppRoutes.routes.connections.routes.xapi.route}
          status={
            <StatusTag
              enabled={xapiConfigured}
              label={xapiConfigured ? "Configured" : "Not configured"}
            />
          }
          details={
            xapiConfigured
              ? `Authentication: ${xapiAuthType}`
              : "Learning Record Store is not configured"
          }
        />

        <ConnectionCard
          title="Webhooks"
          icon="pi-link"
          testId="connection-card-webhooks"
          path={AdminAppRoutes.routes.connections.routes.webhooks.route}
          status={<StatusTag enabled={webhookEnabled} />}
          details={
            webhookEnabled && webhookEndpoint
              ? `Endpoint: ${webhookEndpoint}`
              : "Webhook notifications are not configured"
          }
        />
      </div>
    </>
  );
};

export default Connections;
